import { useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { FormattedMessage, useIntl } from 'react-intl'
import { Pressable, View } from 'react-native'
import styled from 'styled-components'
import { phoneNumbers } from '@flix/common/config/companyDetails'
import { MaxWidthView } from '@flix/common/elements/layout'
import { H2, H3, Paragraph } from '@flix/common/elements/typography'
import { cloudinaryUrl } from '@flix/common/services/cloudinary'
import { media } from '@flix/common/style/media'
import formMsgs from '../../../components/forms/messages'
import { canonicalUrl } from '../../../utils/htmlHead'
import meta from '../../metaDescriptions'
import { FilmPartnersForm } from './FilmPartnersForm'
import FilmPartnersPAModal from './FilmPartnersPAModal'
import messages from './messages'

const paModelCloudinaryId = 'pa-model'
const paModelImgSrcLg = cloudinaryUrl(paModelCloudinaryId)
const paModelImgSrc = cloudinaryUrl(paModelCloudinaryId, { width: 2330, height: 352 })

const MaxWidthContainer = styled(MaxWidthView)`
  padding-top: ${(props) => props.theme.spacing.large}px;
  padding-left: ${(props) => props.theme.spacing.small}px;
  padding-right: ${(props) => props.theme.spacing.small}px;

  ${media.sm`
    padding-top: ${(props) => props.theme.spacing.xxlarge}px;
  `}
`

const DetailsAndInputRow = styled(View)`
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  margin-bottom: ${(props) => props.theme.spacing.xxlarge}px;
`

const PAModel = styled(View)`
  align-items: center;
  padding: 0 0 ${(props) => props.theme.spacing.medium}px;
  margin-bottom: ${(props) => props.theme.spacing.large}px;
`

const PaddedView = styled(View)`
  flex: 1;
  min-width: 350px;
  padding: ${(props) => props.theme.spacing.large}px;
  margin: ${(props) => props.theme.spacing.medium}px;
  background: ${(props) => props.theme.colors.grayDarker};
`

const FilmPartnersPage = ({ location }) => {
  const intl = useIntl()
  const [modal, setModal] = useState(false)
  const paModel = useRef(null)

  const handleToggleModal = () => {
    // On a phone it's really awkward to view the image in a modal,
    // so we open it directly in the window instead.
    paModel.current.offsetWidth < 640
      ? (window.location.href = paModelImgSrcLg)
      : setModal((prev) => !prev)
  }

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage(meta.filmPartnerTitle)}</title>
        <meta property="og:title" content="Become a Film Partner of Flix Premiere" />
        <meta
          name="description"
          property="og:description"
          content={intl.formatMessage(meta.filmPartner)}
        />
        <meta property="og:url" content={canonicalUrl(location)} />
        <link rel="canonical" href={canonicalUrl(location)} />
      </Helmet>

      <MaxWidthContainer alignItems="flex-start">
        <H2>
          <FormattedMessage {...messages.heading} />
        </H2>

        <Paragraph mb="large">
          <FormattedMessage {...messages.description} />
        </Paragraph>

        <Pressable onPress={handleToggleModal}>
          <PAModel ref={paModel}>
            <H3 mb="none">
              <FormattedMessage {...messages.lifecycle} />
            </H3>
            <FormattedMessage tagName="small" {...messages.enlarge} />
            <br />
            <img className="img-responsive" src={paModelImgSrc} alt="" />
          </PAModel>
        </Pressable>

        <Paragraph mb="medium" style={{ alignSelf: 'center' }}>
          <FormattedMessage {...messages.moreInfo} />
        </Paragraph>

        <DetailsAndInputRow>
          <PaddedView>
            <FormattedMessage tagName="dt" {...messages.usaOffice} />
            <Paragraph>{phoneNumbers.US}</Paragraph>
            <FormattedMessage tagName="dt" {...messages.ukOffice} />
            <Paragraph>{phoneNumbers.GB}</Paragraph>
            <FormattedMessage tagName="dt" {...formMsgs.email} />
            <Paragraph>
              <a
                className="plain"
                href="mailto:partners@flixpremiere.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                partners@flixpremiere.com
              </a>
            </Paragraph>
          </PaddedView>
          <PaddedView>
            <FilmPartnersForm />
          </PaddedView>
        </DetailsAndInputRow>
      </MaxWidthContainer>

      {modal && (
        <FilmPartnersPAModal
          imgSrc={paModelImgSrc}
          imgSrcFullScreen={paModelImgSrcLg}
          onRequestClose={handleToggleModal}
        />
      )}
    </>
  )
}

export default FilmPartnersPage
